import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { ArrowLeft, Settings, DollarSign, Users, Briefcase, Save, Upload, Loader2, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPlans() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushingToStripe, setPushingToStripe] = useState(null);
  const [plans, setPlans] = useState([]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/plans`, {
        headers: getAuthHeaders()
      });
      setPlans(response.data.plans || []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to fetch plans');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchPlans();
  }, [navigate, fetchPlans]);

  const handlePlanChange = (planId, field, value) => {
    setPlans(prev => prev.map(plan => 
      plan.id === planId ? { ...plan, [field]: value } : plan
    ));
  };

  const handleSavePlan = async (planId) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/plans/${planId}`,
        {
          price: plan.price,
          max_jobs_per_month: plan.max_jobs_per_month,
          max_team_members: plan.max_team_members,
          max_expenses_per_month: plan.max_expenses_per_month,
          has_logo: plan.has_logo,
          has_stripe: plan.has_stripe,
          has_receipt_photos: plan.has_receipt_photos,
          has_mileage: plan.has_mileage,
          has_billable_expenses: plan.has_billable_expenses,
          has_native_sms: plan.has_native_sms,
          has_native_email: plan.has_native_email,
          has_auto_reminders: plan.has_auto_reminders,
          has_recurring: plan.has_recurring,
          has_image_download: plan.has_image_download,
          image_download_price: plan.image_download_price,
          has_crm: plan.has_crm,
          max_emails_per_month: plan.max_emails_per_month,
          max_sms_per_month: plan.max_sms_per_month
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`${planId} plan updated locally`);
      
      // Offer to push to Stripe for paid plans
      if (planId !== 'free') {
        toast.info('Remember to push pricing to Stripe for new subscribers', {
          action: {
            label: 'Push to Stripe',
            onClick: () => handlePushToStripe(planId)
          }
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    } finally {
      setSaving(false);
    }
  };

  const handlePushToStripe = async (planId) => {
    setPushingToStripe(planId);
    try {
      const response = await axios.post(
        `${API}/admin/plans/${planId}/push-to-stripe`,
        { migrate_existing: false },
        { headers: getAuthHeaders() }
      );
      toast.success(`Pricing pushed to Stripe: $${response.data.amount}/mo`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push to Stripe');
    } finally {
      setPushingToStripe(null);
    }
  };

  const getPlanDisplayName = (id) => {
    const names = { free: 'Side Hustle', solo: 'Solo Pro', crew: 'Crew' };
    return names[id] || id;
  };

  const getPlanColor = (id) => {
    const colors = { 
      free: 'border-slate-600', 
      solo: 'border-[#FF5F00]', 
      crew: 'border-purple-600' 
    };
    return colors[id] || 'border-slate-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      
      <div className="min-h-screen bg-slate-900">
        {/* Header */}
        <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="flex items-center gap-2">
              <Settings className="w-6 h-6 text-red-500" />
              <h1 className="text-xl font-bold text-white">Subscription Plans</h1>
            </div>
          </div>
        </header>

        <main className="p-6">
          <div className="max-w-6xl mx-auto">
            <p className="text-slate-400 mb-6">
              Configure pricing and limits for each subscription tier. Changes affect new signups and renewals.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {plans.map(plan => (
                <div 
                  key={plan.id}
                  className={`bg-slate-800 rounded-xl border-2 ${getPlanColor(plan.id)} p-6`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-white">{getPlanDisplayName(plan.id)}</h2>
                    {plan.id === 'solo' && (
                      <span className="bg-[#FF5F00] text-white text-xs font-bold px-2 py-1 rounded-full">
                        POPULAR
                      </span>
                    )}
                  </div>

                  <div className="space-y-4">
                    {/* Price */}
                    <div>
                      <Label className="text-slate-400 text-xs">Monthly Price ($)</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          type="number"
                          step="1"
                          value={plan.price}
                          onChange={(e) => handlePlanChange(plan.id, 'price', parseFloat(e.target.value) || 0)}
                          className="pl-10 bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    </div>

                    {/* Max Jobs */}
                    <div>
                      <Label className="text-slate-400 text-xs">Max Jobs/Month (empty = unlimited)</Label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          type="number"
                          value={plan.max_jobs_per_month || ''}
                          onChange={(e) => handlePlanChange(plan.id, 'max_jobs_per_month', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="Unlimited"
                          className="pl-10 bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    </div>

                    {/* Max Team Members */}
                    <div>
                      <Label className="text-slate-400 text-xs">Max Team Members</Label>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          type="number"
                          value={plan.max_team_members}
                          onChange={(e) => handlePlanChange(plan.id, 'max_team_members', parseInt(e.target.value) || 1)}
                          className="pl-10 bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    </div>

                    {/* Max Expenses */}
                    <div>
                      <Label className="text-slate-400 text-xs">Max Expenses/Month (empty = unlimited)</Label>
                      <Input
                        type="number"
                        value={plan.max_expenses_per_month || ''}
                        onChange={(e) => handlePlanChange(plan.id, 'max_expenses_per_month', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="Unlimited"
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>

                    {/* Feature Toggles */}
                    <div className="pt-4 border-t border-slate-700 space-y-3">
                      <p className="text-slate-400 text-xs font-bold uppercase">Features</p>
                      
                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300 text-sm">Custom Logo</Label>
                        <Switch
                          checked={plan.has_logo}
                          onCheckedChange={(v) => handlePlanChange(plan.id, 'has_logo', v)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300 text-sm">Stripe Payments</Label>
                        <Switch
                          checked={plan.has_stripe}
                          onCheckedChange={(v) => handlePlanChange(plan.id, 'has_stripe', v)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300 text-sm">Receipt Photos</Label>
                        <Switch
                          checked={plan.has_receipt_photos}
                          onCheckedChange={(v) => handlePlanChange(plan.id, 'has_receipt_photos', v)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300 text-sm">Mileage Tracking</Label>
                        <Switch
                          checked={plan.has_mileage}
                          onCheckedChange={(v) => handlePlanChange(plan.id, 'has_mileage', v)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300 text-sm">Billable Expenses</Label>
                        <Switch
                          checked={plan.has_billable_expenses}
                          onCheckedChange={(v) => handlePlanChange(plan.id, 'has_billable_expenses', v)}
                        />
                      </div>
                    </div>

                    {/* Communication Features */}
                    <div className="pt-4 border-t border-slate-700 space-y-3">
                      <p className="text-slate-400 text-xs font-bold uppercase">Communication</p>
                      
                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300 text-sm">Native SMS (Twilio)</Label>
                        <Switch
                          checked={plan.has_native_sms}
                          onCheckedChange={(v) => handlePlanChange(plan.id, 'has_native_sms', v)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300 text-sm">In-App Email</Label>
                        <Switch
                          checked={plan.has_native_email}
                          onCheckedChange={(v) => handlePlanChange(plan.id, 'has_native_email', v)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300 text-sm">Auto Reminders</Label>
                        <Switch
                          checked={plan.has_auto_reminders}
                          onCheckedChange={(v) => handlePlanChange(plan.id, 'has_auto_reminders', v)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300 text-sm">Recurring Appointments</Label>
                        <Switch
                          checked={plan.has_recurring}
                          onCheckedChange={(v) => handlePlanChange(plan.id, 'has_recurring', v)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300 text-sm">Client Management (CRM)</Label>
                        <Switch
                          checked={plan.has_crm}
                          onCheckedChange={(v) => handlePlanChange(plan.id, 'has_crm', v)}
                        />
                      </div>
                    </div>

                    {/* Message Limits */}
                    <div className="pt-4 border-t border-slate-700 space-y-3">
                      <p className="text-slate-400 text-xs font-bold uppercase">Message Limits</p>
                      
                      <div>
                        <Label className="text-slate-300 text-sm">Emails/Month (empty = unlimited)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={plan.max_emails_per_month ?? ''}
                          onChange={(e) => handlePlanChange(plan.id, 'max_emails_per_month', e.target.value ? parseInt(e.target.value) : null)}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                          placeholder="Unlimited"
                        />
                      </div>

                      <div>
                        <Label className="text-slate-300 text-sm">SMS/Month (empty = unlimited)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={plan.max_sms_per_month ?? ''}
                          onChange={(e) => handlePlanChange(plan.id, 'max_sms_per_month', e.target.value ? parseInt(e.target.value) : null)}
                          className="bg-slate-700 border-slate-600 text-white mt-1"
                          placeholder="Unlimited"
                        />
                      </div>
                    </div>

                    {/* Data Export Features */}
                    <div className="pt-4 border-t border-slate-700 space-y-3">
                      <p className="text-slate-400 text-xs font-bold uppercase">Data Export</p>
                      
                      <div className="flex items-center justify-between">
                        <Label className="text-slate-300 text-sm">Image Download</Label>
                        <Switch
                          checked={plan.has_image_download}
                          onCheckedChange={(v) => handlePlanChange(plan.id, 'has_image_download', v)}
                        />
                      </div>
                      
                      {/* Image Download Price - only show when feature is enabled */}
                      {plan.has_image_download && (
                        <div className="ml-4 pl-4 border-l-2 border-slate-600">
                          <Label className="text-slate-400 text-xs flex items-center gap-1">
                            <Download className="w-3 h-3" />
                            Download Price ($)
                          </Label>
                          <div className="relative mt-1">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={plan.image_download_price ?? 4.99}
                              onChange={(e) => handlePlanChange(plan.id, 'image_download_price', parseFloat(e.target.value) || 0)}
                              className="pl-10 bg-slate-700 border-slate-600 text-white"
                              placeholder="4.99"
                            />
                          </div>
                          <p className="text-slate-500 text-xs mt-1">
                            One-time fee for users to download all photos
                          </p>
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={() => handleSavePlan(plan.id)}
                      disabled={saving}
                      className="w-full mt-4 bg-slate-700 hover:bg-slate-600"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    
                    {plan.id !== 'free' && (
                      <Button
                        onClick={() => handlePushToStripe(plan.id)}
                        disabled={pushingToStripe === plan.id}
                        variant="outline"
                        className="w-full mt-2 border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                      >
                        {pushingToStripe === plan.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Push to Stripe
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Info Note */}
            <div className="mt-8 bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <p className="text-slate-400 text-sm">
                <strong className="text-white">Note:</strong> Changes to pricing will apply to new subscriptions. 
                Click "Push to Stripe" to update Stripe with your new pricing for new subscribers.
                To migrate existing subscribers to the new price, use the <a href="/admin/stripe" className="text-purple-400 hover:underline">Stripe Management</a> page.
              </p>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
